import { useState } from "react";
import { useShortlistStore, type Candidate } from "../state/useShortlistStore";
import { useMap } from "../map/useMapStore";
import { euro, pricePerM2 } from "../lib/format";
import { SectionTitle } from "./ui/Field";

const ENV_COLORS = ["#ccc", "#cfe6d4", "#f6c445", "#cf3a2e"];

/** Asking €/m² against what comparable flats actually sold for nearby. */
function verdict(c: Candidate): { text: string; tone: string } | null {
  if (!c.askingPrice || !c.surface || !c.localMedian) return null;
  const askM2 = c.askingPrice / c.surface;
  const diff = ((askM2 - c.localMedian) / c.localMedian) * 100;
  const rounded = Math.abs(diff).toFixed(0);
  if (Math.abs(diff) < 5) {
    return { text: "in line with recent sales nearby", tone: "text-ink-soft" };
  }
  return diff > 0
    ? { text: `${rounded}% above recent sales nearby`, tone: "text-[#cf3a2e]" }
    : { text: `${rounded}% below recent sales nearby`, tone: "text-[#2e7d32]" };
}

export function Shortlist() {
  const candidates = useShortlistStore((s) => s.candidates);
  const remove = useShortlistStore((s) => s.remove);
  const update = useShortlistStore((s) => s.update);
  const map = useMap();
  const [openId, setOpenId] = useState<string | null>(null);

  if (candidates.length === 0) return null;

  return (
    <section className="mt-4 border-t border-line pt-3">
      <SectionTitle>Shortlist ({candidates.length})</SectionTitle>
      <ul className="space-y-2">
        {candidates.map((c) => {
          const v = verdict(c);
          const open = openId === c.id;
          return (
            <li key={c.id} className="rounded-lg bg-surface-alt px-2.5 py-2">
              <div className="flex items-start gap-2">
                <button
                  onClick={() => map?.flyTo({ center: [c.lon, c.lat], zoom: 15.5 })}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-[12px] font-medium">
                    {c.label}
                  </span>
                  <span className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-soft">
                    {c.localMedian && <span>{pricePerM2(c.localMedian)} area</span>}
                    {c.green !== undefined && <span>green {c.green}</span>}
                    {c.air !== undefined && (
                      <span
                        className="inline-block h-2 w-2 rounded-sm"
                        style={{ background: ENV_COLORS[c.air] }}
                        title="air quality"
                      />
                    )}
                    {c.noise !== undefined && (
                      <span
                        className="inline-block h-2 w-2 rounded-sm"
                        style={{ background: ENV_COLORS[c.noise] }}
                        title="noise"
                      />
                    )}
                  </span>
                </button>
                <button
                  onClick={() => setOpenId(open ? null : c.id)}
                  className="rounded px-1 text-[11px] text-ink-soft hover:bg-surface"
                >
                  {open ? "−" : "edit"}
                </button>
                <button
                  onClick={() => remove(c.id)}
                  className="rounded px-1 text-[11px] text-ink-soft hover:bg-surface"
                  aria-label="Remove"
                >
                  ✕
                </button>
              </div>

              {v && (
                <p className={`mt-1 text-[11px] ${v.tone}`}>
                  {c.askingPrice && c.surface
                    ? `${euro(c.askingPrice)} · ${c.surface} m² · ${pricePerM2(
                        c.askingPrice / c.surface,
                      )} — `
                    : ""}
                  {v.text}
                </p>
              )}

              {open && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex gap-1.5">
                    <input
                      type="number"
                      placeholder="Asking €"
                      value={c.askingPrice ?? ""}
                      onChange={(e) =>
                        update(c.id, {
                          askingPrice: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      className="w-full rounded border border-line bg-surface px-1.5 py-1 text-[11px] outline-none focus:border-accent"
                    />
                    <input
                      type="number"
                      placeholder="m²"
                      value={c.surface ?? ""}
                      onChange={(e) =>
                        update(c.id, {
                          surface: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      className="w-20 rounded border border-line bg-surface px-1.5 py-1 text-[11px] outline-none focus:border-accent"
                    />
                  </div>
                  <textarea
                    placeholder="Notes — what you liked, what worried you…"
                    value={c.notes ?? ""}
                    onChange={(e) => update(c.id, { notes: e.target.value })}
                    rows={2}
                    className="w-full resize-none rounded border border-line bg-surface px-1.5 py-1 text-[11px] outline-none focus:border-accent"
                  />
                </div>
              )}
              {!open && c.notes && (
                <p className="mt-1 line-clamp-2 text-[11px] text-ink-soft">{c.notes}</p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
